import { translateBlockValue } from 'i18n';
import { Dictionary, groupBy } from 'lodash';
import { ArgumentT, BudgetProposalT } from 'routes/BudgetModule/config/types';

/**
 * Groups budget proposals by their section (investment or operating).
 *
 * @param budgetProposals - The array of budget proposals to be grouped.
 * @returns A record where the keys are budget sections (investment and operating)
 * and the values are arrays of budget proposals belonging to each section.
 */
export const groupBudgetProposalsBySection = (
    budgetProposals: BudgetProposalT[],
): Dictionary<BudgetProposalT[]> => {
    return groupBy(budgetProposals, 'section');
};

/**
 * Compare two arguments to sort them depending on budgetName, resourceName, section then title.
 */
export const compareArgumentsWithResourceName = (
    firstArgument: ArgumentT,
    secondArgument: ArgumentT,
): number => {
    const {
        budgetProposal: firstBP,
        resourceName: firstRN,
        title: firstTitle,
    } = firstArgument;
    const {
        budgetProposal: secondBP,
        resourceName: secondRN,
        title: secondTitle,
    } = secondArgument;

    // Sort by budgetProposal.budgetName
    if (firstBP.budgetName !== secondBP.budgetName) {
        return firstBP.budgetName.localeCompare(secondBP.budgetName);
    }

    // Sort by resourceName
    if (firstRN !== secondRN) {
        return (firstRN ?? '').localeCompare(secondRN ?? '');
    }

    // Sort by budgetProposal.section (if both exist)
    if (firstBP.section && secondBP.section) {
        if (firstBP.section !== secondBP.section) {
            return translateBlockValue(firstBP.section, true).localeCompare(
                translateBlockValue(secondBP.section, true),
            );
        }
    }

    // Sort by title
    return firstTitle.localeCompare(secondTitle);
};
